import {
  ChangeDetectorRef,
  Component,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  ProductResponse,
  ZentraComponent,
} from '../../../interfaces/subProduct.interface';
import { SubProductsService } from '../../../Services/sub-products.service';
import { MatStepper } from '@angular/material/stepper';
import { LoadingService } from '../../../Services/Loading/loading-service.service';

import { SubComponentsService } from '../../../Services/sub-components.service';
import { BackendAccessService } from '../../../Services/APICalls/backend-access-service';
import { MatDialog } from '@angular/material/dialog';
import { ZentraAdminSuccessDialogComponent } from '../zentra-admin-success-dialog/zentra-admin-success-dialog.component';
import { ActivatedRoute } from '@angular/router';
import { todayOrFutureDateValidator } from '../../Integrator/integrator-stepper/integrator-stepper.component';
import { buildComponentLineItems } from '../../_shared-admin/utils/subscription-component-payload.util';
import { AuthService } from '../../../Services/Auth/auth.service';
import { ConnectComponentService } from '../../../Services/connect-component.service';

@Component({
  selector: 'app-zentra-admin-stepper',
  templateUrl: './zentra-admin-stepper.component.html',
  styleUrls: ['./zentra-admin-stepper.component.scss'],

  standalone: false,
})
export class ZentraAdminStepperComponent {
  @ViewChild('stepper') stepper!: MatStepper;
  isLinear = true;
  firstFormGroup!: FormGroup;
  secondFormGroup!: FormGroup;
  products: ProductResponse[] = [];
  selectedProduct: ProductResponse | null = null;
  componentOptions: ZentraComponent[] = [];
  selectedComponents: { component: ZentraComponent; quantity: number }[] = [];
  totalPrice: number = 0;
  selectedComponent: ZentraComponent | null = null;
  quantity: number = 1;
  isSaveButtonDisabled: boolean = true;
  isEditing: boolean = false;
  originalFormValues: any = null;
  isDataReady: boolean = false;
  purchaserEmailErrorMessage: string = '';
  failedPurchaserEmail: string = '';

  constructor(
    readonly formBuilder: FormBuilder,
    readonly productService: SubProductsService,
    readonly subComponentsService: SubComponentsService,
    readonly loadingService: LoadingService,
    readonly cdr: ChangeDetectorRef,
    readonly backend: BackendAccessService,
    readonly dialog: MatDialog,
    readonly route: ActivatedRoute,
    readonly renderer: Renderer2,
    readonly authService: AuthService,
    readonly connectComponentService: ConnectComponentService
  ) {}
  /* istanbul ignore next */
  ngOnInit() {
    this.initializeForms();

    // Show loading indicator
    this.loadingService.show();

    // Fetch products and component options sequentially before processing subscription data
    Promise.all([this.fetchComponentOptions(), this.fetchZentraProducts()])
      .then(() => {
        this.isDataReady = true;

        console.log('Data fetching completed');
        console.log('Component Options:', this.componentOptions);
        console.log('Products:', this.products);

        this.route.paramMap.subscribe((params) => {
          const recordId = params.get('recordId');
          if (recordId) {
            console.log('Record ID:', recordId);
            this.isEditing = true;
            this.fetchSubscriptionData(recordId); // Fetch subscription data after products and components are fetched
          } else {
            this.isEditing = false;
            this.initializeNewSubscription();
          }
        });

        // Listen for form changes to enable/disable the Save button
        this.firstFormGroup.valueChanges.subscribe(() =>
          this.checkForChanges()
        );
        this.secondFormGroup.valueChanges.subscribe(() =>
          this.checkForChanges()
        );
      })
      .catch((error) => {
        console.error('Error during initialization:', error);
      })
      .finally(() => {
        this.loadingService.hide(); // Ensure spinner is hidden in all cases
      });

    // Listen for component changes
    this.components.valueChanges.subscribe(() => {
      this.updateSelectedComponents();
    });

    const engageCtrl = this.metafieldsGroup.get('engageMigration');
    const expectedMigrationDateCtrl = this.metafieldsGroup.get(
      'expectedMigrationDate'
    );
    const engageSiteNameCtrl = this.metafieldsGroup.get('engageSiteName');
    const engageSiteIdCtrl = this.metafieldsGroup.get('engageSiteId');

    engageCtrl?.valueChanges.subscribe((value) => {
      if (value === 'yes') {
        expectedMigrationDateCtrl?.enable();
        engageSiteNameCtrl?.enable();
        engageSiteIdCtrl?.enable();

        expectedMigrationDateCtrl?.setValidators([
          Validators.required,
          todayOrFutureDateValidator(),
        ]);
      } else {
        expectedMigrationDateCtrl?.reset();
        engageSiteNameCtrl?.reset();
        engageSiteIdCtrl?.reset();

        expectedMigrationDateCtrl?.clearValidators();
        expectedMigrationDateCtrl?.disable();
        engageSiteNameCtrl?.disable();
        engageSiteIdCtrl?.disable();
      }

      expectedMigrationDateCtrl?.updateValueAndValidity();
      engageSiteNameCtrl?.updateValueAndValidity();
      engageSiteIdCtrl?.updateValueAndValidity();
    });

    // Initialize control states based on current value
    if (engageCtrl?.value !== 'yes') {
      expectedMigrationDateCtrl?.disable();
      engageSiteNameCtrl?.disable();
      engageSiteIdCtrl?.disable();
    }

    // Clear purchaser email error when the value changes
    this.secondFormGroup.get('purchaserEmail')?.valueChanges.subscribe(() => {
      this.purchaserEmailErrorMessage = '';
      this.failedPurchaserEmail = '';
    });

    this.cdr.detectChanges();
  }

  /* istanbul ignore next */
  checkForChanges() {
    if (!this.isEditing) {
      this.isSaveButtonDisabled = false; // Enable Save for new configurations
      return;
    }

    // Compare current values with original values
    const currentValues = {
      firstForm: this.firstFormGroup.value,
      secondForm: this.secondFormGroup.value,
    };
    this.isSaveButtonDisabled =
      JSON.stringify(currentValues) === JSON.stringify(this.originalFormValues);
  }
  /* istanbul ignore next */
  markAllControlsTouched(): void {
    this.firstFormGroup.markAllAsTouched();
    this.secondFormGroup.markAllAsTouched();
  }
  /* istanbul ignore next */
  fetchSubscriptionData(recordId: string) {
    if (!this.isDataReady) {
      console.warn(
        'Data is not ready yet. Waiting for products and components to load.'
      );
      this.loadingService.hide();
      console.log('Spinner hidden because data is not ready');
      return;
    }
    this.loadingService.show();

    // Ensure component options are fetched before populating the form
    if (this.componentOptions.length === 0) {
      console.error('Component options are not available yet.');
      this.loadingService.hide();
      return;
    }

    this.backend.getConfiguredSubByRecordId(recordId).subscribe({
      next: (data) => {
        this.populateFormWithSubscriptionData(data);
        console.log('Subscription data fetched successfully:', data);

        // Save original form values for comparison
        this.originalFormValues = {
          firstForm: this.firstFormGroup.value,
          secondForm: this.secondFormGroup.value,
        };

        this.isSaveButtonDisabled = true;
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error fetching subscription data:', error);
        this.loadingService.hide();
      },
    });
  }
  /* istanbul ignore next */
  fetchZentraProducts(): Promise<void> {
    this.loadingService.show();
    return new Promise((resolve, reject) => {
      this.productService.getZentraProducts().subscribe({
        next: (products) => {
          this.products = products;
          console.log('Products fetched successfully:', products);
          this.loadingService.hide();
          resolve();
        },
        error: (error) => {
          console.error('Error fetching products:', error);
          this.loadingService.hide();
          reject(new Error(`Error fetching products: ${error}`));
        },
      });
    });
  }
  /* istanbul ignore next */
  fetchComponentOptions(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.subComponentsService.fetchComponentOptions().then(() => {
        this.subComponentsService.getComponentOptions().subscribe({
          next: (components) => {
            this.componentOptions = components;
            console.log('Component Options:', this.componentOptions);
            resolve(); // Resolve the promise after components are fetched
          },
          error: (error) => {
            console.error('Error fetching component options:', error);
            reject(new Error(`Error fetching component options: ${error}`));
          },
        });
      });
    });
  }

  /* istanbul ignore next */
  populateFormWithSubscriptionData(data: any) {
    if (!data) {
      console.error('No subscription data available');
      return;
    }

    const productId = data.subscriptionData.product_id ?? data.maxioProductId;
    console.log('Product ID:', productId);

    const matchingProduct = this.products.find(
      (product) => product.product.id.toString() === productId.toString()
    );
    console.log('Matching Product:', matchingProduct);

    if (matchingProduct) {
      this.selectedProduct = matchingProduct;
      this.firstFormGroup.patchValue({
        maxioProductId: productId,
        product_id: productId,
        product_name: matchingProduct.product.name,
        subscriptionIdentifier: data.subscriptionIdentifier,
      });
    } else {
      console.error('No matching product found for ID:', productId);
    }

    this.secondFormGroup.patchValue({
      purchaserEmail: data.purchaserEmail,
      next_billing_at: data.subscriptionData.next_billing_at,
      metafields: {
        integratorName: data.subscriptionData.metafields.integratorName,
        integratorEmail: data.subscriptionData.metafields.integratorEmail,
        integratorPhone: data.subscriptionData.metafields.integratorPhone,
        projectType: data.subscriptionData.metafields.projectType,
        implementationStartDate:
          data.subscriptionData.metafields.implementationStartDate,
        expectedOccupancyDate:
          data.subscriptionData.metafields.expectedOccupancyDate,
        engageMigration: data.subscriptionData.metafields.engageMigration,
        expectedMigrationDate:
          data.subscriptionData.metafields.expectedMigrationDate,
        engageSiteName: data.subscriptionData.metafields.engageSiteName,
        engageSiteId: data.subscriptionData.metafields.engageSiteId,
      },
    });

    this.components.clear();
    data.subscriptionData.components.forEach((componentData: any) => {
      const componentControl = this.createComponentControl();
      const componentId = componentData.component_id.toString();
      console.log('Component ID:', componentId);

      const matchingComponent = this.componentOptions.find(
        (comp) => comp.id.toString() === componentId
      );
      console.log('Matching Component:', matchingComponent);

      if (matchingComponent) {
        componentControl.patchValue({
          component: matchingComponent,
          allocatedQuantity: componentData.allocated_quantity,
          useCustomPrice: !!componentData.custom_price,
          customPrice: componentData.custom_price ?? '',
        });

        // Enable custom price control if applicable
        if (componentControl.get('useCustomPrice')?.value) {
          componentControl.get('customPrice')?.enable();
        }

        this.connectComponentService.applyConnectFieldValidators(
          componentControl
        );
        if (this.connectComponentService.isConnectComponent(componentControl)) {
          componentControl.patchValue({
            numberOfUnits:
              data.subscriptionData.metafields.number_of_units ?? null,
            numberOfPerimeterAmenity:
              data.subscriptionData.metafields.number_of_perimeter_amenity ??
              null,
          });
        }
      } else {
        console.error('No matching component found for ID:', componentId);
      }

      this.components.push(componentControl);
    });

    this.updateSelectedComponents();
  }
  /* istanbul ignore next */
  initializeNewSubscription() {
    this.firstFormGroup.reset();
    this.secondFormGroup.reset();
    this.components.clear();
    this.addComponent();
    this.selectedComponents = [];
    this.totalPrice = 0;
  }
  /* istanbul ignore next */
  public initializeForms(): void {
    this.firstFormGroup = this.formBuilder.group({
      maxioProductId: ['', Validators.required],
      product_id: ['', Validators.required],
      product_name: [''],
      subscriptionIdentifier: ['', Validators.required],
    });

    this.secondFormGroup = this.formBuilder.group({
      components: this.formBuilder.array([this.createComponentControl()]),
      metafields: this.formBuilder.group({
        projectType: ['', Validators.required],
        implementationStartDate: [
          '',
          [Validators.required, todayOrFutureDateValidator()],
        ],
        expectedOccupancyDate: [
          '',
          [Validators.required, todayOrFutureDateValidator()],
        ],
        engageMigration: ['', Validators.required],
        expectedMigrationDate: ['', todayOrFutureDateValidator()],
        engageSiteName: [''],
        engageSiteId: [''],
        integratorName: [''],
        integratorEmail: ['', Validators.email],
        integratorPhone: ['', [cleanPhoneNumberValidator(10)]],
      }),
      purchaserEmail: ['', [Validators.required, strictEmailValidatorAdmin]],
      customPrice: [''],
      next_billing_at: ['', futureDateValidator()],
    });
  }
  /* istanbul ignore next */
  get metafieldsGroup(): FormGroup {
    return this.secondFormGroup.get('metafields') as FormGroup;
  }
  /* istanbul ignore next */
  createComponentControl(): FormGroup {
    return this.formBuilder.group({
      component: ['', Validators.required],
      allocatedQuantity: [
        1,
        [Validators.required, Validators.min(1), integerValidator()],
      ],
      useCustomPrice: [false],
      customPrice: [
        { value: '', disabled: true },
        [customPriceValidator(), Validators.min(0.01)],
      ],
      numberOfUnits: [null],
      numberOfPerimeterAmenity: [null],
    });
  }
  /* istanbul ignore next */
  get components(): FormArray {
    return this.secondFormGroup.get('components') as FormArray;
  }
  /* istanbul ignore next */
  addComponent(): void {
    this.components.push(this.createComponentControl());
  }
  /* istanbul ignore next */
  removeComponent(index: number): void {
    if (this.components.length > 1) {
      this.components.removeAt(index);
      this.updateSelectedComponents();
    } else {
      console.warn('At least one component is required.');
    }
  }
  /* istanbul ignore next */
  toggleCustomPrice(index: number): void {
    const control = this.components.at(index);
    const customPriceControl = control.get('customPrice');
    const useCustomPrice = control.get('useCustomPrice')?.value;

    if (useCustomPrice) {
      customPriceControl?.enable();
      customPriceControl?.setValidators([
        Validators.required,
        Validators.min(0.01),
      ]);
    } else {
      customPriceControl?.disable();
      customPriceControl?.reset();
      customPriceControl?.clearValidators();
    }

    customPriceControl?.updateValueAndValidity();
  }
  /* istanbul ignore next */
  shouldShowNextButton(): boolean {
    return (
      (this.stepper?.selectedIndex ?? this.getStepCount()) <
      this.getStepCount() - 1
    );
  }
  /* istanbul ignore next */
  shouldShowSaveButton(): boolean {
    return (this.stepper?.selectedIndex ?? -1) === this.getStepCount() - 1;
  }
  /* istanbul ignore next */
  private getStepCount(): number {
    return this.stepper?.steps?.length || 0;
  }
  /* istanbul ignore next */
  selectProduct(product: ProductResponse): void {
    this.selectedProduct = product;
    const productId = product.product.id.toString();
    this.firstFormGroup.patchValue({
      maxioProductId: productId,
      product_id: productId,
      product_name: product.product.name,
    });
  }
  /* istanbul ignore next */
  updateSelectedComponents(): void {
    this.selectedComponents = this.components.controls
      .map((control) => {
        const component = control.get('component')?.value;
        const quantity = control.get('allocatedQuantity')?.value ?? 1;
        return component ? { component, quantity } : null;
      })
      .filter((item) => item !== null) as {
      component: ZentraComponent;
      quantity: number;
    }[];
    this.calculateTotalPrice();
  }
  /* istanbul ignore next */
  onComponentChange(component: ZentraComponent | null, index: number): void {
    if (!component) {
      console.warn('Component not found:', component);
      return;
    }

    // Check if the selected component is already chosen in another control
    const isDuplicate = this.components.controls.some((control, i) => {
      return (
        i !== index && control.get('component')?.value?.id === component.id
      );
    });

    if (isDuplicate) {
      alert(
        'This component is already selected. Please choose a different one.'
      );
      this.components.at(index).get('component')?.reset();
      return;
    }

    this.components.at(index).patchValue({
      component: component,
      allocatedQuantity:
        this.components.at(index).get('allocatedQuantity')?.value ?? 1,
    });
    this.connectComponentService.applyConnectFieldValidators(
      this.components.at(index) as FormGroup
    );

    this.updateSelectedComponents();
  }

  isComponentSelected(
    component: ZentraComponent,
    currentIndex: number
  ): boolean {
    return this.components.controls.some((control, index) => {
      return (
        index !== currentIndex &&
        control.get('component')?.value?.id === component.id
      );
    });
  }
  /* istanbul ignore next */
  displayComponentName(component: ZentraComponent | null): string {
    return component ? component.name : '';
  }
  /* istanbul ignore next */
  calculateTotalPrice(): void {
    const componentsTotal = this.selectedComponents.reduce(
      (total, item, index) => {
        const componentControl = this.components.at(index);
        const useCustomPrice = componentControl.get('useCustomPrice')?.value;
        const customPrice = parseFloat(
          componentControl.get('customPrice')?.value ?? '0'
        );

        const unitPrice = useCustomPrice
          ? customPrice
          : parseFloat(item.component.prices?.[0]?.unitPrice ?? '0');
        return total + unitPrice * item.quantity;
      },
      0
    );

    this.totalPrice = componentsTotal;
  }
  /* istanbul ignore next */
  private handleSubmitResponse(response: any): void {
    // Check if the response indicates a failure
    if (response?.isSuccess === false && response?.errors) {
      this.failedPurchaserEmail = response.errors;
      this.purchaserEmailErrorMessage =
        'This user is already assigned a role that conflicts with this invitation.';
      this.loadingService.hide();
      this.isSaveButtonDisabled = false;
    } else {
      this.purchaserEmailErrorMessage = '';
      this.failedPurchaserEmail = '';
      this.dialog.open(ZentraAdminSuccessDialogComponent, {
        width: '500px',
        disableClose: true,
      });
      this.loadingService.hide();
    }
  }

  /* istanbul ignore next */
  private handleSubmitError(error: any, operation: string): void {
    console.error(`${operation} API Error:`, error);
    this.loadingService.hide();
    this.isSaveButtonDisabled = false;
  }

  /* istanbul ignore next */
  submitForm() {
    this.loadingService.show();
    this.isSaveButtonDisabled = true;
    const firstFormData = this.firstFormGroup.value;
    const secondFormData = this.secondFormGroup.value;
    const metafieldsData = this.metafieldsGroup.value;
    const recordId = this.route.snapshot.paramMap.get('recordId');

    const payload = {
      maxioProductId: firstFormData.maxioProductId.toString(),
      purchaserEmail: secondFormData.purchaserEmail,
      subscriptionIdentifier: firstFormData.subscriptionIdentifier ?? undefined,

      subscriptionData: {
        product_name: firstFormData.product_name,
        product_handle: firstFormData.product_handle ?? undefined,
        product_id: firstFormData.product_id,
        total_pre_tax_price: this.totalPrice.toString(),
        product_price_point_id:
          firstFormData.product_price_point_id ?? undefined,
        next_billing_at: secondFormData.next_billing_at,
        components: buildComponentLineItems(
          this.selectedComponents,
          this.components
        ),
        metafields: {
          integratorName: metafieldsData.integratorName ?? undefined,
          integratorEmail: metafieldsData.integratorEmail ?? undefined,
          integratorPhone: metafieldsData.integratorPhone ?? undefined,
          projectType: metafieldsData.projectType ?? undefined,
          implementationStartDate:
            metafieldsData.implementationStartDate ?? undefined,
          engageMigration: metafieldsData.engageMigration ?? undefined,
          expectedOccupancyDate:
            metafieldsData.expectedOccupancyDate ?? undefined,
          engageSiteName: metafieldsData.engageSiteName ?? undefined,
          engageSiteId: metafieldsData.engageSiteId ?? undefined,
          expectedMigrationDate:
            metafieldsData.expectedMigrationDate ?? undefined,
          number_of_units:
            this.connectComponentService
              .getConnectComponentControl(this.components)
              ?.get('numberOfUnits')?.value?.toString() ?? undefined,
          number_of_perimeter_amenity:
            this.connectComponentService
              .getConnectComponentControl(this.components)
              ?.get('numberOfPerimeterAmenity')?.value?.toString() ??
            undefined,
        },
      },
    };

    console.log('Combined Form Data:', payload);

    if (recordId) {
      // Edit existing configuration
      const editedPayload = { ...payload, recordId }; // Add recordId to the payload
      this.backend.submitEditedConfiguredSubData(editedPayload).subscribe({
        next: (response: any) => {
          console.log('Edit API Response:', response);
          this.handleSubmitResponse(response);
        },
        error: (error) => this.handleSubmitError(error, 'Edit'),
      });
    } else {
      // Create new configuration
      this.backend.submitConfiguredSubData(payload).subscribe({
        next: (response: any) => {
          console.log('Create API Response:', response);
          this.handleSubmitResponse(response);
        },
        error: (error) => this.handleSubmitError(error, 'Create'),
      });
    }
  }

  /* istanbul ignore next */
  goToNextStep() {
    const currentStep = this.stepper.selectedIndex;
    const formGroups = [this.firstFormGroup, this.secondFormGroup];

    if (formGroups[currentStep]?.valid) {
      this.logFormData();
      this.stepper.next();
    }
  }
  /* istanbul ignore next */
  goToPreviousStep() {
    if (this.stepper) {
      this.stepper.previous();
    }
  }
  /* istanbul ignore next */
  isCurrentStepValid() {
    if (!this.stepper) {
      console.log('Stepper is not initialized');
      return false;
    }

    const currentStep = this.stepper.selectedIndex;
    const formGroups = [this.firstFormGroup, this.secondFormGroup];

    return formGroups[currentStep]?.valid ?? false;
  }
  /* istanbul ignore next */
  logFormData() {
    const firstFormData = this.firstFormGroup.value;
    const secondFormData = this.secondFormGroup.value;

    console.log('First Form Data:', firstFormData);
    console.log('Second Form Data:', secondFormData);
  }
}

/* istanbul ignore next */
export function futureDateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const selectedDate = control.value;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set the time to the start of the day

    return selectedDate && selectedDate <= today
      ? { notFutureDate: true }
      : null;
  };
}

/* istanbul ignore next */
export function customPriceValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const parent = control.parent;
    if (!parent) return null;

    const useCustomPrice = parent.get('useCustomPrice')?.value;
    const customPrice = control.value;

    if (useCustomPrice) {
      if (
        customPrice === null ||
        customPrice === undefined ||
        customPrice <= 0
      ) {
        return { requiredCustomPrice: true };
      }
    }
    return null;
  };
}

/* istanbul ignore next */
export function cleanPhoneNumberValidator(minLength: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const rawValue = control.value;
    if (!rawValue) {
      return null; // No value, no error
    }

    // Remove spaces, hyphens, and the '+' sign
    const cleanedValue = rawValue.replace(/[\s\-+]/g, '');

    // Check if the cleaned value meets the minimum length requirement
    if (cleanedValue.length < minLength) {
      return {
        minlength: {
          requiredLength: minLength,
          actualLength: cleanedValue.length,
        },
      };
    }

    return null;
  };
}

/* istanbul ignore next */
export function integerValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null; // Don't validate empty here, use required validator if needed
    }

    // Check if value is an integer (no decimals)
    const isInteger = Number.isInteger(Number(value));

    return !isInteger ? { notInteger: true } : null;
  };
}
/* istanbul ignore next */
export function strictEmailValidatorAdmin(
  control: AbstractControl
): ValidationErrors | null {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const value = control.value;
  if (value && !emailRegex.test(value)) {
    return { invalidEmail: true };
  }
  return null;
}
